import AddMemberModal from "@web/components/AddMemberModal";
import MemberRow from "@web/components/MemberRow";
import { useCallback, useEffect, useState } from "react";
import { useAuth, useToast } from "@/contexts";
import {
  addOrgMember,
  getOrgMembers,
  removeOrgMember,
  transferOrgOwnership,
  updateOrgMemberRole,
} from "@/features/organizations/api";
import type { OrganizationMember, OrgRole } from "@/types";

export default function OrgMembers() {
  const { user } = useAuth();
  const toast = useToast();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<OrgRole>("member");
  const [showAddModal, setShowAddModal] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!user) return;
    const { data, error } = await getOrgMembers("current");
    if (error) {
      toast.showAlert("Error", error.message, "error");
      setLoading(false);
      return;
    }
    if (data) {
      setMembers(data);
      const me = data.find((m) => m.userId === user.id);
      if (me) {
        setCurrentUserRole(me.role);
        setOrgId(me.role === "owner" ? "current" : "current");
      }
    }
    setLoading(false);
  }, [user, toast]);

  useEffect(() => {
    if (!user) return;
    loadMembers();
  }, [user, loadMembers]);

  async function handlePromote(targetUserId: string) {
    const { error } = await updateOrgMemberRole("current", targetUserId, "admin");
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      setMembers((prev) =>
        prev.map((m) => (m.userId === targetUserId ? { ...m, role: "admin" as OrgRole } : m)),
      );
      toast.showAlert("Promoted", "Member promoted to admin", "success");
    }
  }

  async function handleDemote(targetUserId: string) {
    const { error } = await updateOrgMemberRole("current", targetUserId, "member");
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      setMembers((prev) =>
        prev.map((m) => (m.userId === targetUserId ? { ...m, role: "member" as OrgRole } : m)),
      );
      toast.showAlert("Demoted", "Admin demoted to member", "success");
    }
  }

  async function handleRemove(targetUserId: string, displayName: string) {
    toast.showConfirm("Remove member", `Remove ${displayName} from the organization?`, async () => {
      const { error } = await removeOrgMember("current", targetUserId);
      if (error) {
        toast.showAlert("Error", error.message, "error");
      } else {
        setMembers((prev) => prev.filter((m) => m.userId !== targetUserId));
        toast.showAlert("Removed", `${displayName} removed from organization`, "success");
      }
    });
  }

  async function handleTransferOwnership(targetUserId: string) {
    const { error } = await transferOrgOwnership("current", targetUserId);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      setMembers((prev) =>
        prev.map((m) => {
          if (m.userId === targetUserId) return { ...m, role: "owner" as OrgRole };
          if (m.userId === user?.id) return { ...m, role: "admin" as OrgRole };
          return m;
        }),
      );
      setCurrentUserRole("admin");
      toast.showAlert("Ownership transferred", `Ownership transferred successfully`, "success");
    }
  }

  async function handleAddMember(targetUserId: string) {
    const { error } = await addOrgMember("current", targetUserId);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      loadMembers();
      toast.showAlert("Added", "Member added to organization", "success");
    }
  }

  const isAdmin = currentUserRole === "admin" || currentUserRole === "owner";

  return (
    <div className="flex flex-col gap-4">
      {isAdmin && (
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="bg-aqua rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer"
        >
          + Add Member
        </button>
      )}

      {loading && (
        <div className="animate-pulse flex flex-col gap-2">
          {Array.from({ length: 4 }).map(() => {
            const key = crypto.randomUUID();
            return (
              <div
                key={key}
                className="bg-card rounded-[20px] p-4 h-18 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)]"
              />
            );
          })}
        </div>
      )}

      {!loading && members.length === 0 && (
        <div className="text-text-muted text-sm text-center py-8">No members found.</div>
      )}

      {!loading &&
        members.map((member) => (
          <MemberRow
            key={member.userId}
            userId={member.userId}
            displayName={member.displayName}
            role={member.role}
            joinedAt={member.joinedAt}
            currentUserId={user?.id ?? ""}
            currentUserRole={currentUserRole}
            onPromote={handlePromote}
            onDemote={handleDemote}
            onRemove={handleRemove}
            onTransferOwnership={handleTransferOwnership}
          />
        ))}

      {showAddModal && orgId && (
        <AddMemberModal
          orgId={orgId}
          onAdd={handleAddMember}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}
